import { useEffect, useState } from 'react'
import { useReadCompanyRevenue } from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { Dropdown, RevenueChart } from '@/components'
import { formatCurrency } from '@/helpers'
import { MonthsEnum } from '@/types'
import {
  Box,
  Card,
  Stack,
  Typography,
  ButtonBase,
  Skeleton,
} from '@mui/material'
import { getMonth, getYear } from 'date-fns'

const CURRENT_YEAR = getYear(new Date())
const CURRENT_MONTH = getMonth(new Date()) + 1

const YEARS_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map(
  (year) => ({
    label: year.toString(),
    value: year,
  }),
)

export function RevenueData() {
  const [year, setYear] = useState(CURRENT_YEAR)
  const [selectedMonth, setSelectedMonth] = useState(CURRENT_MONTH)

  const { company } = useCompany()

  const { data: revenue, isLoading } = useReadCompanyRevenue({
    company_key: company.key,
    year,
  })

  useEffect(() => {
    if (year === CURRENT_YEAR) return
    setSelectedMonth(1)
  }, [year])

  if (isLoading) {
    return <Loading />
  }

  if (!revenue) {
    return <p>Não foi possível carregar o faturamento</p>
  }

  const monthRevenue =
    revenue.find((item) => item.month === selectedMonth)?.revenue || 0

  const revenueByDesc = revenue.sort((a, b) => b.month - a.month)

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="end"
        color="primary.light"
        mb={[2, 4]}
      >
        <Typography variant="h2" fontSize={40} textAlign="center">
          <Typography component="span" variant="h3">
            R$
          </Typography>{' '}
          {formatCurrency(monthRevenue, { style: 'decimal' })}
        </Typography>

        <Dropdown
          label={year.toString()}
          options={YEARS_OPTIONS}
          onSelect={(value) => setYear(value as number)}
        />
      </Box>

      <Box height={[180, 300]}>
        <RevenueChart mode="dark" month={selectedMonth} year={year} />
      </Box>

      <Box mt={3}>
        <Typography variant="h5" mb={2}>
          Meses anteriores
        </Typography>

        <Box height="calc(100vh - 520px)" overflow="auto" pb={12}>
          <Stack spacing={1}>
            {revenueByDesc?.map(({ month, revenue }) => (
              <Card
                key={month}
                variant="outlined"
                component={ButtonBase}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  borderColor: selectedMonth === month ? 'secondary.main' : '',
                  borderWidth: 2,
                }}
                onClick={() => setSelectedMonth(month)}
              >
                <Typography>{MonthsEnum[month]}</Typography>
                <Typography fontSize={18}>{formatCurrency(revenue)}</Typography>
              </Card>
            ))}
          </Stack>
        </Box>
      </Box>
    </>
  )
}

const Loading = () => {
  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="end"
        mb={[1, 2]}
      >
        <Skeleton width={160} sx={{ fontSize: 48 }} />
        <Skeleton width={80} sx={{ fontSize: 24 }} />
      </Box>

      <Box height={[180, 300]}>
        <Skeleton variant="rounded" height="100%" />
      </Box>

      <Box mt={3}>
        <Skeleton width={200} sx={{ fontSize: 24, mb: 1 }} />

        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={46} />
          ))}
        </Stack>
      </Box>
    </>
  )
}
